import React, { useState, useRef, useEffect } from 'react';
import { motion, useDragControls, useMotionValue, useTransform, useScroll, PanInfo, AnimatePresence } from 'framer-motion';
import './PhotoCarousel.css';

const PhotoCarousel: React.FC = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const [selectedImage, setSelectedImage] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);
    const carouselRef = useRef<HTMLDivElement>(null);
    const sectionRef = useRef<HTMLDivElement>(null);
    const dragControls = useDragControls();
    const x = useMotionValue(0);

    // Scroll-based animations
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start end", "end start"]
    });

    const headerY = useTransform(scrollYProgress, [0.05, 0.25], [40, 0]);
    const headerOpacity = useTransform(scrollYProgress, [0.05, 0.25], [0, 1]);
    const containerScale = useTransform(scrollYProgress, [0.1, 0.3], [0.95, 1]);
    const containerOpacity = useTransform(scrollYProgress, [0.1, 0.3], [0, 1]);

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0, scale: 0.95 },
        visible: {
            opacity: 1,
            scale: 1,
            transition: {
                duration: 0.8,
                ease: [0.25, 1, 0.5, 1] as const
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30, scale: 0.9 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6,
                ease: [0.25, 1, 0.5, 1] as const
            }
        }
    };

    // Disable right-click, drag, and other context menus
    const handleContextMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        return false;
    };

    const handleDragStart = (e: any) => {
        e.preventDefault();
        return false;
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        // Prevent text selection and drag
        if (e.detail === 2) {
            e.preventDefault();
            return false;
        }
    };

    // Obfuscated image data - split and encoded
    const imageData = [
        {
            id: 1,
            src: btoa('https://res.cloudinary.com/dhqmcmekn/image/upload/v1765204556/PAYEL_24x36_yy7hv8.webp'),
            fullImage: btoa('https://res.cloudinary.com/dhqmcmekn/image/upload/v1765204556/PAYEL_24x36_yy7hv8.webp'),
            alt: "Pre-Wedding photography work 1"
        },
        {
            id: 2,
            src: btoa('https://res.cloudinary.com/dhqmcmekn/image/upload/v1765205412/1000238557_iwg9la.webp'),
            fullImage: btoa('https://res.cloudinary.com/dhqmcmekn/image/upload/v1765205412/1000238557_iwg9la.webp'),
            alt: "Pre-Wedding photography work 2"
        },
        {
            id: 3,
            src: btoa('https://res.cloudinary.com/dhqmcmekn/image/upload/v1764752452/02_tyyv00.webp'),
            fullImage: btoa('https://res.cloudinary.com/dhqmcmekn/image/upload/v1764752452/02_tyyv00.webp'),
            alt: "Pre-Wedding photography work 3"
        },
        {
            id: 4,
            src: btoa('https://res.cloudinary.com/dhqmcmekn/image/upload/v1764752455/23_ennkn9.webp'),
            fullImage: btoa('https://res.cloudinary.com/dhqmcmekn/image/upload/v1764752455/23_ennkn9.webp'),
            alt: "Pre-Wedding photography work 4"
        }
    ];

    // Decode function to get actual URLs
    const decodeImage = (encoded: string) => {
        try {
            return atob(encoded);
        } catch {
            return '';
        }
    };

    const photos = imageData.map(img => ({
        ...img,
        src: decodeImage(img.src),
        fullImage: decodeImage(img.fullImage)
    }));

    // Create infinite array by duplicating photos
    const infinitePhotos = [...photos, ...photos];

    // Modal functions
    const openModal = (index: number) => {
        setSelectedImage(index);
        setIsLoading(true);
        document.body.style.overflow = 'hidden';
    };

    const closeModal = () => {
        setSelectedImage(null);
        setIsLoading(false);
        document.body.style.overflow = 'unset';
    };

    const nextImage = () => {
        if (selectedImage !== null) {
            setIsLoading(true);
            setSelectedImage((selectedImage + 1) % photos.length);
        }
    };

    const prevImage = () => {
        if (selectedImage !== null) {
            setIsLoading(true);
            setSelectedImage((selectedImage - 1 + photos.length) % photos.length);
        }
    };

    const handleImageLoad = () => {
        setIsLoading(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') closeModal();
        if (e.key === 'ArrowRight') nextImage();
        if (e.key === 'ArrowLeft') prevImage();
    };

    const minSwipeDistance = 50;

    const onTouchStart = (e: React.TouchEvent) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e: React.TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            nextImage();
        } else if (isRightSwipe) {
            prevImage();
        }
    };

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const handleDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
        const offset = info.offset.x;
        const threshold = 50;

        if (offset > threshold) {
            // Swipe right - go to previous
            setCurrentIndex(prev => {
                const newIndex = prev - 1;
                if (newIndex < 0) {
                    // Wrap around to the end
                    setTimeout(() => setCurrentIndex(photos.length - 1), 0);
                    return photos.length - 1;
                }
                return newIndex;
            });
        } else if (offset < -threshold) {
            // Swipe left - go to next
            setCurrentIndex(prev => {
                const newIndex = prev + 1;
                if (newIndex >= photos.length) {
                    // Wrap around to the beginning
                    setTimeout(() => setCurrentIndex(0), 0);
                    return 0;
                }
                return newIndex;
            });
        }
    };

    const goToSlide = (index: number) => {
        setCurrentIndex(index);
    };

    const goToPrevious = () => {
        setCurrentIndex(prev => {
            const newIndex = prev - 1;
            return newIndex < 0 ? photos.length - 1 : newIndex;
        });
    };

    const goToNext = () => {
        setCurrentIndex(prev => {
            const newIndex = prev + 1;
            return newIndex >= photos.length ? 0 : newIndex;
        });
    };

    return (
        <section className="photo-carousel-section" ref={sectionRef}>
            <motion.div
                className="photo-carousel-header"
                style={{ y: headerY, opacity: headerOpacity }}
            >
                <h3>FEATURED WORKS</h3>
                <div className="carousel-line"></div>
            </motion.div>
            
            {isMobile ? (
                <motion.div
                    className="mobile-photo-carousel"
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, margin: "-100px" }}
                >
                    <motion.div
                        ref={carouselRef}
                        className="mobile-carousel-container"
                        drag="x"
                        dragControls={dragControls}
                        dragConstraints={{ left: 0, right: 0 }}
                        dragElastic={0.2}
                        onDragEnd={handleDragEnd}
                        style={{ x }}
                    >
                        <motion.div
                            className="mobile-carousel-track"
                            animate={{ x: -currentIndex * 100 + "%" }}
                            transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        >
                            {infinitePhotos.map((photo, index) => (
                                <motion.div
                                    key={`${photo.id}-${index}`}
                                    className="mobile-photo-item"
                                    variants={itemVariants}
                                    onClick={() => openModal(index % photos.length)}
                                    onContextMenu={handleContextMenu}
                                    onDragStart={handleDragStart}
                                    onMouseDown={handleMouseDown}
                                >
                                    <img 
                                        src={photo.src} 
                                        alt={photo.alt}
                                        onContextMenu={handleContextMenu}
                                        onDragStart={handleDragStart}
                                        style={{ pointerEvents: 'none' }}
                                    />
                                    <div className="photo-overlay"></div>
                                </motion.div>
                            ))}
                        </motion.div>
                    </motion.div>

                    <motion.div
                        className="mobile-carousel-controls"
                        variants={itemVariants}
                    >
                        <button 
                            className="mobile-nav-btn prev" 
                            onClick={goToPrevious}
                        >
                            ←
                        </button>
                        
                        <div className="mobile-carousel-dots">
                            {photos.map((_, index) => (
                                <button
                                    key={index}
                                    className={`mobile-dot ${index === currentIndex ? 'active' : ''}`}
                                    onClick={() => goToSlide(index)}
                                />
                            ))}
                        </div>
                        
                        <button 
                            className="mobile-nav-btn next" 
                            onClick={goToNext}
                        >
                            →
                        </button>
                    </motion.div>
                </motion.div>
            ) : (
                <motion.div
                    className="photo-carousel-container"
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, margin: "-100px" }}
                    style={{ scale: containerScale, opacity: containerOpacity }}
                >
                    {photos.map((photo, index) => (
                        <motion.div
                            key={photo.id}
                            className="photo-item"
                            variants={itemVariants}
                            custom={index}
                            onClick={() => openModal(index)}
                            onContextMenu={handleContextMenu}
                            onDragStart={handleDragStart}
                            onMouseDown={handleMouseDown}
                        >
                            <img 
                                src={photo.src} 
                                alt={photo.alt}
                                onContextMenu={handleContextMenu}
                                onDragStart={handleDragStart}
                                style={{ pointerEvents: 'none' }}
                            />
                            <div className="photo-overlay"></div>
                        </motion.div>
                    ))}
                </motion.div>
            )}

            {/* Image Modal */}
            <AnimatePresence>
                {selectedImage !== null && (
                    <motion.div
                        className="image-modal"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeModal}
                        onKeyDown={handleKeyDown}
                        tabIndex={0}
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                    >
                        <motion.div
                            className="modal-content"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button className="modal-close" onClick={closeModal}>
                                ×
                            </button>
                            
                            <button className="modal-nav prev" onClick={prevImage}>
                                ←
                            </button>
                            
                            <div className="modal-image-container">
                                {isLoading && (
                                    <div className="image-loader">
                                        <div className="spinner"></div>
                                    </div>
                                )}
                                <img 
                                    src={photos[selectedImage].fullImage}
                                    alt={photos[selectedImage].alt}
                                    className="modal-image"
                                    loading="lazy"
                                    onLoad={handleImageLoad}
                                    style={{ opacity: isLoading ? 0 : 1, pointerEvents: 'none' }}
                                    onContextMenu={handleContextMenu}
                                    onDragStart={handleDragStart}
                                />
                            </div>
                            
                            <button className="modal-nav next" onClick={nextImage}>
                                →
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};

export default PhotoCarousel;
